import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Grid,
  Chip,
  Divider,
  IconButton,
  Paper,
  Stack,
  Avatar,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import PersonIcon from '@mui/icons-material/Person';
import EuroSymbolIcon from '@mui/icons-material/EuroSymbol';
import ScheduleIcon from '@mui/icons-material/Schedule';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import HourglassEmptyIcon from '@mui/icons-material/HourglassEmpty';
import EventAvailableIcon from '@mui/icons-material/EventAvailable';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import VisibilityIcon from '@mui/icons-material/Visibility';
import PendingIcon from '@mui/icons-material/Pending';
import AssignmentIcon from '@mui/icons-material/Assignment';

const OfferDetailModal = ({
  open,
  onClose,
  item,
  onUpdateStatus,
  onDownloadPdf,
  onViewCompteRenduDetails,
  currentUserRole,
}) => {
  const [openViewDialog, setOpenViewDialog] = useState(false);
  const [selectedReportToView, setSelectedReportToView] = useState(null);

  if (!item) return null;

  const isOffer = item.item_type === 'offre' || Boolean(item.id_offre);
  const info = item.info_offre || {};
  const intervenant = item.intervenant || {};
  const category = item.category || {};
  const itemId = isOffer ? item.id_offre : item.id_ws;

  const intervenantRole = Number(intervenant.role_user || intervenant.role);
  const loggedUserRole = Number(currentUserRole);

  const isPraticien =
    loggedUserRole === 4 ||
    intervenantRole === 4 ||
    intervenant.role_label?.toLowerCase() === 'praticien';

  const reservations =
    item.offre_reservation || item.offreReservation || item.reservations || [];

  const statusValue = item.is_valide;
  const getStatusConfig = (status) => {
    switch (status) {
      case 'valider':
      case 'valide':
        return {
          label: 'Validé',
          color: 'success',
          icon: <CheckCircleIcon fontSize="small" />,
        };
      case 'refuser':
      case 'refuse':
        return {
          label: 'Refusé',
          color: 'error',
          icon: <CancelIcon fontSize="small" />,
        };
      default:
        return {
          label: 'En attente',
          color: 'warning',
          icon: <HourglassEmptyIcon fontSize="small" />,
        };
    }
  };
  const statusCfg = getStatusConfig(statusValue);

  let joursOffFormatted = 'Aucun';
  try {
    if (info.jours_off) {
      const parsed =
        typeof info.jours_off === 'string'
          ? JSON.parse(info.jours_off)
          : info.jours_off;
      joursOffFormatted =
        Array.isArray(parsed) && parsed.length > 0
          ? parsed.join(', ')
          : 'Aucun';
    }
  } catch (e) {
    joursOffFormatted = info.jours_off || 'Aucun';
  }

  const sectionPaperStyle = {
    p: 2.5,
    height: '100%',
    borderRadius: '16px',
    border: '1px solid #E2E8F0',
    backgroundColor: '#FFFFFF',
    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.03)',
  };

  const handleOpenViewCompteRendu = (compteRendu) => {
    if (onViewCompteRenduDetails) {
      onViewCompteRenduDetails(compteRendu);
    } else {
      setSelectedReportToView(compteRendu);
      setOpenViewDialog(true);
    }
  };

  // 🟢 Fonction de téléchargement / impression PDF robuste
  const handleDownloadPdf = (compteRendu) => {
    if (!compteRendu) return;

    if (typeof onDownloadPdf === 'function') {
      onDownloadPdf(compteRendu);
      return;
    }

    const pdfUrl =
      compteRendu.pdf_url ||
      compteRendu.url_pdf ||
      compteRendu.metadata?.pdf_url;
    if (pdfUrl) {
      window.open(pdfUrl, '_blank');
      return;
    }

    // Génération et impression du PDF propre basée sur le contenu du rapport
    const printWindow = window.open('', '_blank');
    const infoOffre = compteRendu.reservation?.offre?.info_offre || info;
    const offreTitre =
      infoOffre.titre_offre || compteRendu.pratique || 'Prestation SOA';
    const offreLieu = infoOffre.address_ws
      ? `${infoOffre.address_ws}, ${infoOffre.city_ws}`
      : 'Locaux S.O.A';

    const praticien = compteRendu.user || intervenant;
    const praticienNom =
      [praticien.prenom_user, praticien.nom_user].filter(Boolean).join(' ') ||
      'Praticien SOA';

    const htmlContent = `
      <!DOCTYPE html>
      <html lang="fr">
      <head>
        <meta charset="UTF-8">
        <title>Compte Rendu - ${compteRendu.numero_seance || 'Séance'}</title>
        <style>
          body { font-family: 'Helvetica Neue', Helvetica, Arial, sans-serif; color: #1E293B; line-height: 1.6; padding: 40px; background-color: #FFFFFF; }
          .header { border-bottom: 3px solid #3311CC; padding-bottom: 20px; margin-bottom: 25px; display: flex; justify-content: space-between; align-items: flex-start; }
          .logo-area { font-size: 20px; font-weight: 800; color: #3311CC; text-transform: uppercase; letter-spacing: 1px; }
          .title { font-size: 22px; font-weight: bold; color: #0F172A; margin: 5px 0 0 0; }
          .subtitle { font-size: 14px; color: #64748B; margin-top: 5px; }
          .badge { background: #E0F2FE; color: #0284C7; padding: 6px 14px; border-radius: 20px; font-size: 12px; font-weight: bold; text-transform: uppercase; display: inline-block; }
          .info-box { display: grid; grid-template-columns: 1fr 1fr; gap: 20px; background: #F8FAFC; border: 1px solid #E2E8F0; border-radius: 12px; padding: 20px; margin-bottom: 25px; }
          .info-item { font-size: 13px; color: #475569; margin-bottom: 6px; }
          .info-item strong { color: #1E293B; }
          .section { background: #FFFFFF; border: 1px solid #E2E8F0; border-left: 4px solid #0EA5E9; border-radius: 8px; padding: 18px; margin-bottom: 18px; }
          .section.green { border-left-color: #10B981; }
          .section.indigo { border-left-color: #6366F1; }
          .section-title { font-size: 13px; font-weight: bold; text-transform: uppercase; color: #0EA5E9; margin-bottom: 8px; }
          .section.green .section-title { color: #10B981; }
          .section.indigo .section-title { color: #6366F1; }
          .section-text { font-size: 14px; color: #334155; white-space: pre-line; }
          .footer { margin-top: 40px; text-align: center; font-size: 11px; color: #94A3B8; border-top: 1px solid #E2E8F0; padding-top: 15px; }
          @media print { body { padding: 20px; } .section { break-inside: avoid; } }
        </style>
      </head>
      <body>
        <div class="header">
          <div>
            <div class="logo-area">Plateforme S.O.A</div>
            <div class="title">Compte Rendu & Suivi</div>
            <div class="subtitle">Prestation : <strong>${offreTitre}</strong></div>
          </div>
          <div>
            <span class="badge">${compteRendu.statut === 'valide' ? 'Validé & Signé' : 'Brouillon'}</span>
          </div>
        </div>

        <div class="info-box">
          <div>
            <div class="info-item"><strong>Séance :</strong> ${compteRendu.numero_seance || 'Séance n°1'}</div>
            <div class="info-item"><strong>Date de consultation :</strong> ${compteRendu.date_seance || '—'}</div>
            <div class="info-item"><strong>Lieu :</strong> ${offreLieu}</div>
          </div>
          <div>
            <div class="info-item"><strong>Intervenant / Praticien :</strong> ${praticienNom}</div>
            <div class="info-item"><strong>Date de rédaction :</strong> ${compteRendu.created_at ? new Date(compteRendu.created_at).toLocaleString('fr-FR') : '—'}</div>
          </div>
        </div>

        ${compteRendu.pratique ? `<div class="section"><div class="section-title">Pratique & Approche</div><div class="section-text">${compteRendu.pratique}</div></div>` : ''}
        ${compteRendu.perception_patient ? `<div class="section"><div class="section-title">Perception & État du Patient</div><div class="section-text">${compteRendu.perception_patient}</div></div>` : ''}
        ${compteRendu.resume_seance ? `<div class="section green"><div class="section-title">Résumé & Actes Réalisés</div><div class="section-text">${compteRendu.resume_seance}</div></div>` : ''}
        ${compteRendu.prevision_suite ? `<div class="section indigo"><div class="section-title">Plan de Soins & Suite</div><div class="section-text">${compteRendu.prevision_suite}</div></div>` : ''}

        <div class="footer">
          Plateforme S.O.A (Soins d'Onco-Accompagnement) • Document officiel généré le ${new Date().toLocaleDateString('fr-FR')}
        </div>

        <script>
          window.onload = function() { window.print(); window.onafterprint = function() { window.close(); } }
        </script>
      </body>
      </html>
    `;

    printWindow.document.write(htmlContent);
    printWindow.document.close();
  };

  return (
    <>
      <Dialog
        open={open}
        onClose={onClose}
        maxWidth="xl"
        fullWidth
        scroll="paper"
        PaperProps={{
          sx: {
            borderRadius: '24px',
            boxShadow: '0 24px 80px rgba(15, 23, 42, 0.15)',
            overflow: 'hidden',
          },
        }}
      >
        {/* En-tête */}
        <DialogTitle
          sx={{
            m: 0,
            p: 2.5,
            px: 3,
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            background: 'linear-gradient(135deg, #F8FAFC 0%, #EFF6FF 100%)',
            borderBottom: '1px solid #E2E8F0',
          }}
        >
          <Stack direction="row" alignItems="center" spacing={1.5}>
            <Chip
              label={isOffer ? 'Offre de soin' : 'Atelier'}
              color={isOffer ? 'primary' : 'secondary'}
              size="small"
              sx={{ fontWeight: 800, borderRadius: '8px', px: 0.5 }}
            />
            <Typography
              variant="h6"
              sx={{ fontWeight: 900, color: '#1E293B', fontSize: '1.2rem' }}
            >
              Fiche détaillée de la publication
            </Typography>
          </Stack>
          <IconButton
            onClick={onClose}
            size="small"
            sx={{
              color: '#64748B',
              backgroundColor: '#FFFFFF',
              border: '1px solid #E2E8F0',
              '&:hover': { backgroundColor: '#F1F5F9' },
            }}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogTitle>

        {/* Contenu principal */}
        <DialogContent
          sx={{ p: { xs: 2.5, md: 3.5 }, backgroundColor: '#F8FAFC' }}
        >
          <Grid container spacing={3}>
            {/* Section 1 : Titre & Description */}
            <Grid item xs={12}>
              <Paper
                elevation={0}
                sx={{
                  p: 3,
                  borderRadius: '18px',
                  background:
                    'linear-gradient(135deg, #2563EB 0%, #1D4ED8 100%)',
                  color: '#FFFFFF',
                  boxShadow: '0 10px 25px rgba(37, 99, 235, 0.25)',
                }}
              >
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'flex-start',
                    mb: 1.5,
                  }}
                >
                  <Box sx={{ pr: 2 }}>
                    <Typography
                      variant="caption"
                      sx={{
                        textTransform: 'uppercase',
                        letterSpacing: '1px',
                        opacity: 0.8,
                        fontWeight: 700,
                      }}
                    >
                      {category.title_category
                        ? `Catégorie : ${category.title_category}`
                        : 'Offre SOA'}
                    </Typography>
                    <Typography variant="h5" sx={{ fontWeight: 900, mt: 0.5 }}>
                      {isOffer
                        ? info.titre_offre || 'Sans titre'
                        : item.title_ws || 'Sans titre'}
                    </Typography>
                  </Box>
                  <Chip
                    icon={statusCfg.icon}
                    label={`Statut : ${statusCfg.label}`}
                    sx={{
                      fontWeight: 800,
                      backgroundColor: '#FFFFFF',
                      color:
                        statusCfg.color === 'success'
                          ? '#16A34A'
                          : statusCfg.color === 'error'
                            ? '#DC2626'
                            : '#D97706',
                      '& .MuiChip-icon': { color: 'inherit' },
                    }}
                  />
                </Box>
                <Typography
                  variant="body2"
                  sx={{ opacity: 0.9, lineHeight: 1.6, whiteSpace: 'pre-line' }}
                >
                  {isOffer
                    ? info.description_offre || 'Aucune description fournie.'
                    : item.description_ws || 'Aucune description fournie.'}
                </Typography>
              </Paper>
            </Grid>

            {/* Section 2 : Tarifs */}
            <Grid item xs={12} md={6} lg={3}>
              <Paper elevation={0} sx={sectionPaperStyle}>
                <Stack
                  direction="row"
                  alignItems="center"
                  spacing={1}
                  sx={{ mb: 2, color: '#2563EB' }}
                >
                  <EuroSymbolIcon fontSize="small" />
                  <Typography
                    variant="subtitle2"
                    sx={{
                      fontWeight: 800,
                      color: '#0F172A',
                      textTransform: 'uppercase',
                      fontSize: '0.75rem',
                    }}
                  >
                    Tarification & Infos
                  </Typography>
                </Stack>
                <Stack spacing={1.2}>
                  <Box
                    sx={{ display: 'flex', justifyContent: 'space-between' }}
                  >
                    <Typography variant="body2" color="text.secondary">
                      ID Offre :
                    </Typography>
                    <Typography variant="body2" sx={{ fontWeight: 700 }}>
                      #{itemId || 'N/A'}
                    </Typography>
                  </Box>
                  <Box
                    sx={{ display: 'flex', justifyContent: 'space-between' }}
                  >
                    <Typography variant="body2" color="text.secondary">
                      Format :
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{ fontWeight: 700, textTransform: 'capitalize' }}
                    >
                      {info.format_offre || 'Non spécifié'}
                    </Typography>
                  </Box>
                  <Divider sx={{ my: 0.5 }} />
                  <Box
                    sx={{ display: 'flex', justifyContent: 'space-between' }}
                  >
                    <Typography variant="body2" color="text.secondary">
                      Prix Standard :
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{ fontWeight: 800, color: '#16A34A' }}
                    >
                      {info.prix_offre ? `${info.prix_offre} €` : 'Gratuit'}
                    </Typography>
                  </Box>
                  <Box
                    sx={{ display: 'flex', justifyContent: 'space-between' }}
                  >
                    <Typography variant="body2" color="text.secondary">
                      Prix Spécial SOA :
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{ fontWeight: 800, color: '#2563EB' }}
                    >
                      {info.prix_special_soa
                        ? `${info.prix_special_soa} €`
                        : 'Non spécifié'}
                    </Typography>
                  </Box>
                </Stack>
              </Paper>
            </Grid>

            {/* Section 3 : Intervenant */}
            <Grid item xs={12} md={6} lg={3}>
              <Paper elevation={0} sx={sectionPaperStyle}>
                <Stack
                  direction="row"
                  alignItems="center"
                  spacing={1}
                  sx={{ mb: 2, color: '#0D9488' }}
                >
                  <PersonIcon fontSize="small" />
                  <Typography
                    variant="subtitle2"
                    sx={{
                      fontWeight: 800,
                      color: '#0F172A',
                      textTransform: 'uppercase',
                      fontSize: '0.75rem',
                    }}
                  >
                    Professionnel
                  </Typography>
                </Stack>
                <Stack spacing={1.2}>
                  <Stack
                    direction="row"
                    spacing={1.5}
                    alignItems="center"
                    sx={{ mb: 0.5 }}
                  >
                    <Avatar
                      sx={{
                        width: 36,
                        height: 36,
                        bgcolor: '#0D9488',
                        fontWeight: 800,
                        fontSize: 14,
                      }}
                    >
                      {(
                        intervenant.prenom_user?.[0] ||
                        intervenant.nom_user?.[0] ||
                        'P'
                      ).toUpperCase()}
                    </Avatar>
                    <Box sx={{ minWidth: 0 }}>
                      <Typography
                        variant="body2"
                        sx={{ fontWeight: 800, color: '#0F172A' }}
                        noWrap
                      >
                        {intervenant.prenom_user || intervenant.nom_user
                          ? `${intervenant.prenom_user || ''} ${intervenant.nom_user || ''}`.trim()
                          : 'Inconnu'}
                      </Typography>
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        noWrap
                        display="block"
                      >
                        {intervenant.email_user || "Pas d'email"}
                      </Typography>
                    </Box>
                  </Stack>
                  <Divider sx={{ my: 0.5 }} />
                  <Box
                    sx={{ display: 'flex', justifyContent: 'space-between' }}
                  >
                    <Typography variant="body2" color="text.secondary">
                      Rôle :
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{ fontWeight: 700, textTransform: 'capitalize' }}
                    >
                      {intervenant.role_label ||
                        (intervenant.role_user === 4
                          ? 'Praticien'
                          : 'Intervenant')}
                    </Typography>
                  </Box>
                  <Box
                    sx={{ display: 'flex', justifyContent: 'space-between' }}
                  >
                    <Typography variant="body2" color="text.secondary">
                      Commission :
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{ fontWeight: 800, color: '#0F172A' }}
                    >
                      {intervenant.commission
                        ? `${intervenant.commission} %`
                        : 'Non spécifiée'}
                    </Typography>
                  </Box>
                </Stack>
              </Paper>
            </Grid>

            {/* Section 4 : Horaires */}
            <Grid item xs={12} md={6} lg={3}>
              <Paper elevation={0} sx={sectionPaperStyle}>
                <Stack
                  direction="row"
                  alignItems="center"
                  spacing={1}
                  sx={{ mb: 2, color: '#D97706' }}
                >
                  <ScheduleIcon fontSize="small" />
                  <Typography
                    variant="subtitle2"
                    sx={{
                      fontWeight: 800,
                      color: '#0F172A',
                      textTransform: 'uppercase',
                      fontSize: '0.75rem',
                    }}
                  >
                    Horaires & Durée
                  </Typography>
                </Stack>
                <Stack spacing={1.2}>
                  <Box
                    sx={{ display: 'flex', justifyContent: 'space-between' }}
                  >
                    <Typography variant="body2" color="text.secondary">
                      Durée :
                    </Typography>
                    <Typography variant="body2" sx={{ fontWeight: 700 }}>
                      {info.duree_offre ? `${info.duree_offre} min` : '—'}
                    </Typography>
                  </Box>
                  <Box
                    sx={{ display: 'flex', justifyContent: 'space-between' }}
                  >
                    <Typography variant="body2" color="text.secondary">
                      Pause :
                    </Typography>
                    <Typography variant="body2" sx={{ fontWeight: 700 }}>
                      {info.duree_pause ? `${info.duree_pause} min` : '—'}
                    </Typography>
                  </Box>
                  <Box
                    sx={{ display: 'flex', justifyContent: 'space-between' }}
                  >
                    <Typography variant="body2" color="text.secondary">
                      Jours off :
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{
                        fontWeight: 700,
                        color:
                          joursOffFormatted !== 'Aucun' ? '#2632dc' : 'inherit',
                      }}
                    >
                      {joursOffFormatted}
                    </Typography>
                  </Box>
                  <Divider sx={{ my: 0.5 }} />
                  <Box
                    sx={{ display: 'flex', justifyContent: 'space-between' }}
                  >
                    <Typography variant="body2" color="text.secondary">
                      Plages :
                    </Typography>
                    <Typography variant="caption" sx={{ fontWeight: 700 }}>
                      Matin:{' '}
                      {info.plage_horaire_matin_debut?.slice(0, 5) || '--'} -{' '}
                      {info.plage_horaire_matin_fin?.slice(0, 5) || '--'}
                    </Typography>
                  </Box>
                </Stack>
              </Paper>
            </Grid>

            {/* Section 5 : Localisation */}
            <Grid item xs={12} md={6} lg={3}>
              <Paper elevation={0} sx={sectionPaperStyle}>
                <Stack
                  direction="row"
                  alignItems="center"
                  spacing={1}
                  sx={{ mb: 2, color: '#7C3AED' }}
                >
                  <LocationOnIcon fontSize="small" />
                  <Typography
                    variant="subtitle2"
                    sx={{
                      fontWeight: 800,
                      color: '#0F172A',
                      textTransform: 'uppercase',
                      fontSize: '0.75rem',
                    }}
                  >
                    Localisation
                  </Typography>
                </Stack>
                <Stack spacing={1.2}>
                  <Box
                    sx={{ display: 'flex', justifyContent: 'space-between' }}
                  >
                    <Typography variant="body2" color="text.secondary">
                      Lieu :
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{ fontWeight: 700, textTransform: 'capitalize' }}
                    >
                      {info.location_type || 'Non spécifié'}
                    </Typography>
                  </Box>
                  <Box
                    sx={{ display: 'flex', justifyContent: 'space-between' }}
                  >
                    <Typography variant="body2" color="text.secondary">
                      Adresse :
                    </Typography>
                    <Typography
                      variant="caption"
                      align="right"
                      sx={{ fontWeight: 700, maxWidth: '60%' }}
                    >
                      {info.city_ws
                        ? `${info.city_ws} (${info.country_ws || ''})`
                        : 'Non spécifiée'}
                    </Typography>
                  </Box>
                  <Divider sx={{ my: 0.5 }} />
                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                    }}
                  >
                    <Typography variant="body2" color="text.secondary">
                      Déplacement :
                    </Typography>
                    <Chip
                      label={info.is_deplacement_possible ? 'Oui' : 'Non'}
                      size="small"
                      color={
                        info.is_deplacement_possible ? 'success' : 'default'
                      }
                      sx={{ height: 20, fontSize: 11, fontWeight: 700 }}
                    />
                  </Box>
                </Stack>
              </Paper>
            </Grid>

            {/* Section 6 : TABLEAU DES RÉSERVATIONS ET COMPTES RENDUS */}
            <Grid item xs={12}>
              <Paper
                elevation={0}
                sx={{
                  p: 3,
                  borderRadius: '18px',
                  border: '1px solid #E2E8F0',
                  backgroundColor: '#FFFFFF',
                }}
              >
                <Stack
                  direction="row"
                  alignItems="center"
                  spacing={1.5}
                  sx={{ mb: 2.5 }}
                >
                  <EventAvailableIcon sx={{ color: '#2563EB' }} />
                  <Typography
                    variant="h6"
                    sx={{
                      fontWeight: 800,
                      color: '#0F172A',
                      fontSize: '1.05rem',
                    }}
                  >
                    {isPraticien
                      ? `Réservations & Comptes Rendus (${reservations.length})`
                      : `Réservations associées (${reservations.length})`}
                  </Typography>
                </Stack>

                {reservations.length === 0 ? (
                  <Box
                    sx={{
                      p: 3,
                      textAlign: 'center',
                      backgroundColor: '#F8FAFC',
                      borderRadius: '12px',
                      border: '1px dashed #CBD5E1',
                    }}
                  >
                    <Typography variant="body2" color="text.secondary">
                      Aucune réservation enregistrée pour le moment.
                    </Typography>
                  </Box>
                ) : (
                  <TableContainer
                    sx={{
                      border: '1px solid #E2E8F0',
                      borderRadius: '12px',
                      overflow: 'hidden',
                    }}
                  >
                    <Table sx={{ minWidth: 650 }}>
                      <TableHead sx={{ backgroundColor: '#F8FAFC' }}>
                        <TableRow>
                          <TableCell sx={{ fontWeight: 800, color: '#475569' }}>
                            Réf. Réservation
                          </TableCell>
                          <TableCell sx={{ fontWeight: 800, color: '#475569' }}>
                            Patient
                          </TableCell>
                          <TableCell sx={{ fontWeight: 800, color: '#475569' }}>
                            Date & Heure
                          </TableCell>
                          <TableCell sx={{ fontWeight: 800, color: '#475569' }}>
                            Statut Réservation
                          </TableCell>
                          {isPraticien && (
                            <>
                              <TableCell
                                sx={{ fontWeight: 800, color: '#475569' }}
                              >
                                Comptes Rendus & Statuts
                              </TableCell>
                              <TableCell
                                align="center"
                                sx={{ fontWeight: 800, color: '#475569' }}
                              >
                                Actions
                              </TableCell>
                            </>
                          )}
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {reservations.map((res) => {
                          const rawCR =
                            res.comptes_rendus ||
                            res.comptesRendus ||
                            res.comptes_rendus ||
                            res.compteRendu;
                          const listCR = Array.isArray(rawCR)
                            ? rawCR
                            : rawCR
                              ? [rawCR]
                              : [];
                          const hasCompteRendu = listCR.length > 0;

                          let rowBgColor = '#FFFFFF';
                          if (isPraticien) {
                            rowBgColor = '#FEF2F2';
                            if (hasCompteRendu) {
                              const hasAllValid = listCR.every((cr) =>
                                [
                                  'valide',
                                  'valider',
                                  'approved',
                                  'confirmed',
                                ].includes((cr?.statut || '').toLowerCase())
                              );
                              rowBgColor = hasAllValid ? '#F0FDF4' : '#FFFBEB';
                            }
                          }

                          const patientProfile =
                            res.client?.patient_profile ||
                            res.client?.patientProfile;
                          const patientName = patientProfile
                            ? `${patientProfile.prenom_patient || ''} ${patientProfile.nom_patient || ''}`.trim()
                            : res.client
                              ? `${res.client.prenom_client || ''} ${res.client.nom_client || ''}`.trim()
                              : 'Client anonyme';

                          return (
                            <TableRow
                              key={res.id_offre_reservation || res.id}
                              sx={{
                                backgroundColor: rowBgColor,
                                '&:last-child td, &:last-child th': {
                                  border: 0,
                                },
                                '&:hover': { opacity: 0.95 },
                              }}
                            >
                              <TableCell
                                sx={{ fontWeight: 700, color: '#2563EB' }}
                              >
                                #{res.id_offre_reservation || res.id}
                              </TableCell>
                              <TableCell>
                                <Typography
                                  variant="body2"
                                  sx={{ fontWeight: 700, color: '#0F172A' }}
                                >
                                  {patientName}
                                </Typography>
                                {patientProfile?.code_patient && (
                                  <Typography
                                    variant="caption"
                                    color="text.secondary"
                                  >
                                    {patientProfile.code_patient}
                                  </Typography>
                                )}
                              </TableCell>
                              <TableCell
                                sx={{ fontWeight: 600, color: '#475569' }}
                              >
                                {res.start_time_reservation
                                  ? new Date(
                                      res.start_time_reservation
                                    ).toLocaleString('fr-FR')
                                  : '—'}
                              </TableCell>
                              <TableCell>
                                <Chip
                                  label={res.status_reservation}
                                  size="small"
                                  sx={{
                                    fontWeight: 700,
                                    textTransform: 'capitalize',
                                    backgroundColor: '#FFFFFF',
                                    color: '#334155',
                                    border: '1px solid #E2E8F0',
                                  }}
                                />
                              </TableCell>

                              {isPraticien && (
                                <>
                                  <TableCell>
                                    {!hasCompteRendu ? (
                                      <Chip
                                        icon={<PendingIcon />}
                                        label="Manquant"
                                        size="small"
                                        sx={{
                                          backgroundColor: '#FEE2E2',
                                          color: '#DC2626',
                                          fontWeight: 800,
                                        }}
                                      />
                                    ) : (
                                      <Stack spacing={1}>
                                        {listCR.map((cr, idx) => {
                                          const crStatut = (
                                            cr?.statut || 'fait'
                                          ).toLowerCase();
                                          const isValid = [
                                            'valide',
                                            'valider',
                                            'approved',
                                            'confirmed',
                                          ].includes(crStatut);
                                          return (
                                            <Box
                                              key={
                                                cr.id_comptes_rendus ||
                                                cr.id ||
                                                idx
                                              }
                                              sx={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                height: 32,
                                              }}
                                            >
                                              <Chip
                                                icon={
                                                  isValid ? (
                                                    <CheckCircleIcon />
                                                  ) : (
                                                    <PendingIcon />
                                                  )
                                                }
                                                label={`${cr.numero_seance || `CR n°${idx + 1}`} : ${cr.statut || 'Fait'}`}
                                                size="small"
                                                sx={{
                                                  backgroundColor: isValid
                                                    ? '#D1FAE5'
                                                    : '#FEF3C7',
                                                  color: isValid
                                                    ? '#059669'
                                                    : '#D97706',
                                                  fontWeight: 800,
                                                  fontSize: '0.75rem',
                                                  textTransform: 'capitalize',
                                                }}
                                              />
                                            </Box>
                                          );
                                        })}
                                      </Stack>
                                    )}
                                  </TableCell>

                                  <TableCell align="center">
                                    {!hasCompteRendu ? (
                                      <Typography
                                        variant="caption"
                                        color="text.secondary"
                                      >
                                        —
                                      </Typography>
                                    ) : (
                                      <Stack spacing={1} alignItems="center">
                                        {listCR.map((cr, idx) => (
                                          <Stack
                                            direction="row"
                                            spacing={1}
                                            key={
                                              cr.id_comptes_rendus ||
                                              cr.id ||
                                              idx
                                            }
                                            sx={{
                                              height: 32,
                                              alignItems: 'center',
                                            }}
                                          >
                                            <Tooltip
                                              title={`Voir ${cr.numero_seance || `CR n°${idx + 1}`}`}
                                            >
                                              <IconButton
                                                size="small"
                                                onClick={() =>
                                                  handleOpenViewCompteRendu(cr)
                                                }
                                                sx={{
                                                  color: '#2563EB',
                                                  backgroundColor: '#FFFFFF',
                                                  border: '1px solid #CBD5E1',
                                                  '&:hover': {
                                                    backgroundColor: '#DBEAFE',
                                                  },
                                                }}
                                              >
                                                <VisibilityIcon fontSize="small" />
                                              </IconButton>
                                            </Tooltip>

                                            <Tooltip
                                              title={`Télécharger PDF - ${cr.numero_seance || `CR n°${idx + 1}`}`}
                                            >
                                              <IconButton
                                                size="small"
                                                onClick={() =>
                                                  handleDownloadPdf(cr)
                                                }
                                                sx={{
                                                  color: '#DC2626',
                                                  backgroundColor: '#FFFFFF',
                                                  border: '1px solid #CBD5E1',
                                                  '&:hover': {
                                                    backgroundColor: '#FEE2E2',
                                                  },
                                                }}
                                              >
                                                <PictureAsPdfIcon fontSize="small" />
                                              </IconButton>
                                            </Tooltip>
                                          </Stack>
                                        ))}
                                      </Stack>
                                    )}
                                  </TableCell>
                                </>
                              )}
                            </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                  </TableContainer>
                )}
              </Paper>
            </Grid>
          </Grid>
        </DialogContent>

        {/* Actions principales */}
        <DialogActions
          sx={{
            p: 2.5,
            px: 3,
            bgcolor: '#F1F5F9',
            justifyContent: 'space-between',
            borderTop: '1px solid #E2E8F0',
          }}
        >
          <Stack direction="row" spacing={1.5}>
            {isOffer && typeof onUpdateStatus === 'function' && (
              <>
                <Button
                  variant="contained"
                  color="success"
                  size="small"
                  startIcon={<CheckCircleIcon />}
                  onClick={() => onUpdateStatus(itemId, 'valider')}
                  sx={{
                    borderRadius: '10px',
                    textTransform: 'none',
                    fontWeight: 700,
                    boxShadow: 'none',
                  }}
                >
                  Valider l'offre
                </Button>
                <Button
                  variant="outlined"
                  color="error"
                  size="small"
                  startIcon={<CancelIcon />}
                  onClick={() => onUpdateStatus(itemId, 'refuser')}
                  sx={{
                    borderRadius: '10px',
                    textTransform: 'none',
                    fontWeight: 700,
                  }}
                >
                  Refuser
                </Button>
                <Button
                  variant="outlined"
                  color="warning"
                  size="small"
                  startIcon={<HourglassEmptyIcon />}
                  onClick={() => onUpdateStatus(itemId, 'en_attente')}
                  sx={{
                    borderRadius: '10px',
                    textTransform: 'none',
                    fontWeight: 700,
                  }}
                >
                  Mettre en attente
                </Button>
              </>
            )}
          </Stack>
          <Button
            onClick={onClose}
            variant="contained"
            sx={{
              borderRadius: '10px',
              textTransform: 'none',
              fontWeight: 700,
              bgcolor: '#0F172A',
              '&:hover': { bgcolor: '#1E293B' },
              boxShadow: 'none',
              px: 3.5,
            }}
          >
            Fermer
          </Button>
        </DialogActions>
      </Dialog>

      {/* 🟢 MODALE DE VISUALISATION DES DÉTAILS DU COMPTE RENDU SÉLECTIONNÉ */}
      <Dialog
        open={openViewDialog}
        onClose={() => setOpenViewDialog(false)}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: { borderRadius: '20px', padding: '10px' },
        }}
      >
        <DialogTitle
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            pb: 1,
          }}
        >
          <Stack direction="row" spacing={1.5} alignItems="center">
            <Avatar sx={{ bgcolor: '#2563EB', width: 36, height: 36 }}>
              <AssignmentIcon fontSize="small" />
            </Avatar>
            <Box>
              <Typography
                variant="h6"
                sx={{ fontWeight: 800, color: '#1E293B', lineHeight: 1.2 }}
              >
                Détails du Compte Rendu
              </Typography>
              <Typography
                variant="caption"
                color="text.secondary"
                fontWeight={700}
              >
                {selectedReportToView?.numero_seance || 'Séance'} (ID: #
                {selectedReportToView?.id_comptes_rendus ||
                  selectedReportToView?.id}
                )
              </Typography>
            </Box>
          </Stack>
          <IconButton onClick={() => setOpenViewDialog(false)} size="small">
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent dividers>
          {selectedReportToView ? (
            <Stack spacing={2.5}>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    fontWeight={700}
                  >
                    PRATIQUE / OFFRE
                  </Typography>
                  <Typography variant="body1" fontWeight={700} color="#0F172A">
                    {selectedReportToView.pratique || '—'}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    fontWeight={700}
                  >
                    DATE DE LA SÉANCE
                  </Typography>
                  <Typography variant="body1" fontWeight={700} color="#0F172A">
                    {selectedReportToView.date_seance
                      ? new Date(
                          selectedReportToView.date_seance
                        ).toLocaleString('fr-FR')
                      : '—'}
                  </Typography>
                </Grid>
              </Grid>

              <Divider />

              <Box>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  fontWeight={700}
                  display="block"
                  gutterBottom
                >
                  PERCEPTION DU PATIENT
                </Typography>
                <Paper
                  variant="outlined"
                  sx={{ p: 2, bgcolor: '#F8FAFC', borderRadius: '12px' }}
                >
                  <Typography
                    variant="body2"
                    sx={{ whiteSpace: 'pre-line', color: '#334155' }}
                  >
                    {selectedReportToView.perception_patient ||
                      'Aucune information enregistrée.'}
                  </Typography>
                </Paper>
              </Box>

              <Box>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  fontWeight={700}
                  display="block"
                  gutterBottom
                >
                  RÉSUMÉ DE LA SÉANCE
                </Typography>
                <Paper
                  variant="outlined"
                  sx={{ p: 2, bgcolor: '#F8FAFC', borderRadius: '12px' }}
                >
                  <Typography
                    variant="body2"
                    sx={{ whiteSpace: 'pre-line', color: '#334155' }}
                  >
                    {selectedReportToView.resume_seance ||
                      'Aucun résumé enregistré.'}
                  </Typography>
                </Paper>
              </Box>

              <Box>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  fontWeight={700}
                  display="block"
                  gutterBottom
                >
                  PRÉVISIONS POUR LA SUITE
                </Typography>
                <Paper
                  variant="outlined"
                  sx={{ p: 2, bgcolor: '#F8FAFC', borderRadius: '12px' }}
                >
                  <Typography
                    variant="body2"
                    sx={{ whiteSpace: 'pre-line', color: '#334155' }}
                  >
                    {selectedReportToView.prevision_suite ||
                      'Aucune prévision enregistrée.'}
                  </Typography>
                </Paper>
              </Box>
            </Stack>
          ) : (
            <Typography color="text.secondary">
              Aucun détail disponible.
            </Typography>
          )}
        </DialogContent>

        <DialogActions sx={{ p: 2, justifyContent: 'space-between' }}>
          <Button
            startIcon={<PictureAsPdfIcon />}
            color="primary"
            variant="outlined"
            onClick={() => handleDownloadPdf(selectedReportToView)}
            sx={{
              borderRadius: '10px',
              textTransform: 'none',
              fontWeight: 700,
            }}
          >
            Télécharger le PDF
          </Button>

          <Button
            onClick={() => setOpenViewDialog(false)}
            variant="contained"
            sx={{
              borderRadius: '10px',
              textTransform: 'none',
              bgcolor: '#0F172A',
            }}
          >
            Fermer
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default OfferDetailModal;
